<template>
    <main class="flex-1 relative overflow-y-auto py-6 focus:outline-none" tabindex="0">
        <form @submit.prevent="saveLegalLink">
            <div class="max-w-7xl mx-auto sm:px-6 lg:px-8 px-5">
                <div class="md:flex md:items-center md:justify-between">
                    <div class="flex-1 min-w-0">
                        <h1 class="py-0.5 text-2xl font-semibold text-gray-900">{{ $t('Create legal link') }}</h1>
                    </div>
                </div>
            </div>
            <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div class="mt-6 shadow sm:rounded-sm">
                    <loading :status="loading"/>
                    <div class="bg-white md:grid md:grid-cols-3 md:gap-6 px-4 py-5 sm:p-6">
                        <div class="md:col-span-1">
                            <h3 class="text-lg font-medium leading-6 text-gray-900">{{ $t('Legal link details') }}</h3>
                            <p class="mt-1 text-sm leading-5 text-gray-500">
                                {{ $t('Manage legal navigation links for pages such as imprint, privacy policy and terms of use.') }}
                            </p>
                        </div>
                        <div class="mt-5 md:mt-0 md:col-span-2">
                            <div class="grid grid-cols-3 gap-6">
                                <div class="col-span-3">
                                    <label class="block text-sm font-medium leading-5 text-gray-700" for="title_mode">{{ $t('Title source') }}</label>
                                    <div class="mt-1 relative rounded shadow-sm">
                                        <select
                                            id="title_mode"
                                            v-model="legalLink.title_mode"
                                            class="form-select block w-full"
                                            required
                                        >
                                            <option value="translation">{{ $t('Translation key') }}</option>
                                            <option value="custom">{{ $t('Custom title') }}</option>
                                        </select>
                                    </div>
                                </div>
                                <div v-if="legalLink.title_mode === 'translation'" class="col-span-3">
                                    <label class="block text-sm font-medium leading-5 text-gray-700" for="translation_key">{{ $t('Translation key') }}</label>
                                    <div class="mt-1 relative rounded shadow-sm">
                                        <input
                                            id="translation_key"
                                            v-model="legalLink.translation_key"
                                            :placeholder="$t('Translation key')"
                                            class="form-input block w-full"
                                            required
                                        >
                                    </div>
                                    <div class="mt-2 text-xs text-gray-500">
                                        {{ $t('Preview') }}: {{ displayTitle }}
                                    </div>
                                </div>
                                <div v-else class="col-span-3">
                                    <label class="block text-sm font-medium leading-5 text-gray-700" for="custom_title">{{ $t('Custom title') }}</label>
                                    <div class="mt-1 relative rounded shadow-sm">
                                        <input
                                            id="custom_title"
                                            v-model="legalLink.custom_title"
                                            :placeholder="$t('Custom title')"
                                            class="form-input block w-full"
                                            required
                                        >
                                    </div>
                                </div>
                                <div class="col-span-3">
                                    <label class="block text-sm font-medium leading-5 text-gray-700" for="url">{{ $t('URL') }}</label>
                                    <div class="mt-1 relative rounded shadow-sm">
                                        <input
                                            id="url"
                                            v-model="legalLink.url"
                                            :placeholder="$t('URL')"
                                            class="form-input block w-full"
                                        >
                                    </div>
                                </div>
                                <div class="col-span-3 sm:col-span-1">
                                    <label class="block text-sm font-medium leading-5 text-gray-700" for="target">{{ $t('Target') }}</label>
                                    <div class="mt-1 relative rounded shadow-sm">
                                        <select
                                            id="target"
                                            v-model="legalLink.target"
                                            class="form-select block w-full"
                                            required
                                        >
                                            <option value="_self">{{ $t('Same tab') }}</option>
                                            <option value="_blank">{{ $t('New tab') }}</option>
                                        </select>
                                    </div>
                                </div>
                                <div class="col-span-3 sm:col-span-1">
                                    <label class="block text-sm font-medium leading-5 text-gray-700" for="sort_order">{{ $t('Sort order') }}</label>
                                    <div class="mt-1 relative rounded shadow-sm">
                                        <input
                                            id="sort_order"
                                            v-model.number="legalLink.sort_order"
                                            class="form-input block w-full"
                                            min="0"
                                            type="number"
                                        >
                                    </div>
                                </div>
                                <div class="col-span-3 sm:col-span-1">
                                    <label class="block text-sm font-medium leading-5 text-gray-700" for="is_active">{{ $t('Status') }}</label>
                                    <div class="mt-2">
                                        <input-switch
                                            id="is_active"
                                            v-model="legalLink.is_active"
                                            :disabled-label="$t('Inactive')"
                                            :enabled-label="$t('Active')"
                                        ></input-switch>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="stage-footer-surface text-right px-4 py-3 sm:px-6">
                        <div class="inline-flex">
                            <router-link
                                class="btn btn-secondary shadow-sm rounded mr-4"
                                to="/workspace/admin/settings/legal-information"
                            >
                                {{ $t('Cancel') }}
                            </router-link>
                            <button
                                class="btn btn-gray shadow-sm rounded"
                                type="submit"
                            >
                                {{ $t('Create legal link') }}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </form>
    </main>
</template>

<script>
export default {
    name: "legal-links-new",
    metaInfo() {
        return {
            title: this.$i18n.t('Create legal link')
        }
    },
    data() {
        return {
            loading: false,
            legalLink: {
                title_mode: 'translation',
                translation_key: null,
                custom_title: null,
                url: null,
                target: '_self',
                sort_order: 1000,
                is_active: true,
            },
        }
    },
    computed: {
        displayTitle() {
            if (this.legalLink.title_mode === 'translation') {
                return this.translateIfExists(this.legalLink.translation_key) || this.legalLink.translation_key || '-';
            }
            return this.legalLink.custom_title || '-';
        },
    },
    methods: {
        saveLegalLink() {
            const self = this;
            self.loading = true;
            axios.post('api/workspace/admin/legal-links', self.legalLink).then(function (response) {
                self.loading = false;
                self.$notify({
                    title: self.$i18n.t('Success').toString(),
                    text: self.$i18n.t('Data saved correctly').toString(),
                    type: 'success'
                });
                self.$router.push('/workspace/admin/settings/legal-information/' + response.data.legalLink.id + '/edit');
            }).catch(function () {
                self.loading = false;
            });
        },
        translateIfExists(key) {
            const text = String(key || '').trim();
            if (text === '') {
                return '';
            }
            if (typeof this.$te === 'function' && this.$te(text)) {
                return this.$t(text);
            }
            return text;
        },
    }
}
</script>
